import React, { Suspense, useEffect, useRef } from 'react'
import { Canvas } from '@react-three/fiber'
import * as THREE from 'three'
import { Html, useProgress } from '@react-three/drei'
import ForceHierarchyUpdate from './ForceHierarchyUpdate'

interface CanvasWrapperProps {
  children?: React.ReactNode
  enableClickToMove?: boolean
  onClickToMove?: () => void
}

function Loader () {
  const { progress } = useProgress()
  return (
    <Html center className={'cursor-progress'}>
      <span className={'whitespace-nowrap'}>{Math.round((progress + Number.EPSILON) * 100) / 100} % loaded</span>
    </Html>
  )
}

export default function CanvasWrapper (props: CanvasWrapperProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  THREE.Cache.enabled = true

  const startPointerPos: THREE.Vector2 = new THREE.Vector2()
  useEffect(() => {
    if (props.enableClickToMove && canvasRef.current) {
      canvasRef.current?.addEventListener('mousedown', (event: MouseEvent) => {
        startPointerPos.set(event.clientX, event.clientY)
      })

      canvasRef.current?.addEventListener('mouseup', (event: MouseEvent) => {
        if (Math.abs(event.clientX - startPointerPos.x) > 5 ||
          Math.abs(event.clientY - startPointerPos.y) > 5) {
          return
        }

        if (props.onClickToMove) props.onClickToMove()
      })

      canvasRef.current?.addEventListener('touchstart', (event: TouchEvent) => {
        if (event.touches == null || event.touches[0] == null) return
        startPointerPos.set(event.touches[0].clientX, event.touches[0].clientY)
      })

      canvasRef.current?.addEventListener('touchend', (event: TouchEvent) => {
        if (event.touches == null || event.touches[0] == null) return
        if (Math.abs(event.touches[0].clientX - startPointerPos.x) > 5 ||
          Math.abs(event.touches[0].clientY - startPointerPos.y) > 5) {
          return
        }

        if (props.onClickToMove) props.onClickToMove()
      })
    }
  }, [props.enableClickToMove, canvasRef.current])

  return (
    <Canvas ref={canvasRef}
            id={'mainCanvas'}
            className={'viewer'}
            dpr={[1, 2]}>
      <Suspense fallback={<Loader />}>
        {props.children}
        <ForceHierarchyUpdate />
      </Suspense>
    </Canvas>
  )
}
